import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Box, Button, Chip, CircularProgress, Divider, Grid, IconButton, Input, InputAdornment, LinearProgress, Link, Stack, Tooltip, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { AddOutlined, CancelOutlined, EditOutlined, HistoryOutlined, PlayArrowOutlined, RefreshOutlined } from "@mui/icons-material";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import Loader from "../../../UiComponents/Loader/Loader";
import MasterCommonTable from "../../../UiComponents/CommonTable/MasterCommonTable";
import FilterBox from "../../../UiComponents/FilterBox/FilterBox";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
import { getRoadzenCertDownloadUrl, listRoadzenCertificates, recheckRoadzenCertificate } from "../../../Service/RoadzenService";
import { ROADZEN_CREATE_CERT_PATH } from "../roadzenPaths";
import { CancelCertificateDialog, HistoryDialog } from "./RoadzenCertDialogs";
import { STATUS_FILTERS, isSafeLink, rowButtons, rupees, statusColor } from "./roadzenCertUtils";

const ACTION_ICONS = { CONTINUE: PlayArrowOutlined, EDIT: EditOutlined, RECHECK: RefreshOutlined, CANCEL: CancelOutlined, HISTORY: HistoryOutlined };
// the same small icon buttons as the Brisk Certificate List rows
const ROW_ACTION_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  color: "#1E3A5F",
  backgroundColor: "#EEF3F9",
  border: "1px solid #D6E1EE",
  "&:hover": { backgroundColor: "#1E3A5F", borderColor: "#1E3A5F", color: "#fff" },
  "&.Mui-disabled": { opacity: 0.6, color: "#1E3A5F" },
};
const ROW_CANCEL_SX = {
  ...ROW_ACTION_SX,
  color: "#B42318",
  backgroundColor: "#FEF3F2",
  border: "1px solid #FECDCA",
  "&:hover": { backgroundColor: "#B42318", borderColor: "#B42318", color: "#fff" },
  "&.Mui-disabled": { opacity: 0.6, color: "#B42318" },
};

/**
 * RoadZen Certificate List (owner, 2026-11-03: "exactly like Brisk", with Cancel): the certificates made on the RoadZen
 * Create Certificate page. Same header, filters, search, table, row icons and per-row loader as the Brisk Certificate
 * List. The certificate number opens OUR saved PDF. Edit re-issues it at RoadZen (only the changed fields are sent);
 * Cancel refunds the Company Wallet "RoadZen" once; History is this certificate's own story.
 */
const RoadzenCertificateList = () => {
  const navigate = useNavigate();
  const [apiEnv, setApiEnv] = useState(null);
  const [canCreate, setCanCreate] = useState(null); // from the list answer; false hides Create Certificate
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyRows, setBusyRows] = useState({});
  const waitingRowsRef = useRef(new Set());
  const silentReadRef = useRef(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [applied, setApplied] = useState({ startDate: null, endDate: null, status: "" });
  const [showFilter, setShowFilter] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [cancelRow, setCancelRow] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    let alive = true;
    const silent = silentReadRef.current;
    silentReadRef.current = false;
    if (!silent) setLoading(true);
    listRoadzenCertificates({
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch,
      status: applied.status || "",
      startDate: applied.startDate && moment(applied.startDate).isValid() ? moment(applied.startDate).format("YYYY-MM-DD") : "",
      endDate: applied.endDate && moment(applied.endDate).isValid() ? moment(applied.endDate).format("YYYY-MM-DD") : "",
    })
      .then((res) => {
        if (!alive) return;
        if (res.error) {
          ToastError(res.message);
          return;
        }
        setApiEnv(res.data.apiEnv || null);
        setCanCreate(res.data.canCreate === false ? false : null);
        setData(
          (res.data.rows || []).map((row, index) => ({
            ...row,
            _id: row.id,
            sNo: page * rowsPerPage + index + 1,
            certificateText: row.certificateNumber || "-",
            customerText: row.customerName || "-",
            productText: row.product ? row.product.code : "-",
            premiumText: row.totalPremium ? rupees(row.totalPremium) : "-",
            createdByText: row.createdByName || "-",
            formattedDate: row.createdAt ? moment(row.createdAt).format("DD-MM-YYYY HH:mm:ss") : "-",
          }))
        );
        setTotalCount(res.data.total || 0);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
        const finished = [...waitingRowsRef.current];
        waitingRowsRef.current.clear();
        if (finished.length) {
          setBusyRows((prev) => {
            const next = { ...prev };
            finished.forEach((id) => delete next[id]);
            return next;
          });
        }
      });
    return () => {
      alive = false;
    };
  }, [page, rowsPerPage, debouncedSearch, applied, refreshKey]);

  const refresh = () => {
    silentReadRef.current = true;
    setRefreshKey((k) => k + 1);
  };
  const setRowBusy = (id, action) =>
    setBusyRows((prev) => {
      const next = { ...prev };
      if (action) next[id] = action;
      else delete next[id];
      return next;
    });
  /** The row's action is done: its loader stays until the list has been read again. */
  const refreshRow = (id, action) => {
    setRowBusy(id, action);
    waitingRowsRef.current.add(id);
    refresh();
  };

  const download = async (row) => {
    if (busyRows[row.id]) return;
    const tab = window.open("", "_blank");
    setRowBusy(row.id, "DOWNLOAD");
    try {
      const res = await getRoadzenCertDownloadUrl(row.id);
      if (res.error || !res.data || !isSafeLink(res.data.url)) {
        if (tab) tab.close();
        ToastError(res.message || "The certificate PDF is not available right now");
        return;
      }
      if (tab) tab.location.href = res.data.url;
      else window.open(res.data.url, "_blank", "noopener,noreferrer");
    } finally {
      setRowBusy(row.id, "");
    }
  };

  const recheck = async (row) => {
    if (busyRows[row.id]) return;
    setRowBusy(row.id, "RECHECK");
    const res = await recheckRoadzenCertificate(row.id);
    if (res.error) ToastError(res.message);
    else ToastSuccess(res.message);
    refreshRow(row.id, "RECHECK");
  };

  const onAction = (key, row) => {
    if (key === "CONTINUE") navigate(ROADZEN_CREATE_CERT_PATH, { state: { roadzenCert: { mode: "draft", certificateId: row.id } } });
    else if (key === "EDIT") navigate(ROADZEN_CREATE_CERT_PATH, { state: { roadzenCert: { mode: "update", certificateId: row.id } } });
    else if (key === "RECHECK") recheck(row);
    else if (key === "CANCEL") setCancelRow(row);
    else if (key === "HISTORY") setHistoryRow(row);
  };

  const filterData = useMemo(
    () => [
      {
        id: 1,
        label: "Status",
        data: STATUS_FILTERS,
        setState: (e) => {
          const option = STATUS_FILTERS.find((el) => el._id === e);
          if (option) setSelectedStatus(option.value);
        },
        value: (STATUS_FILTERS.find((opt) => opt.value === selectedStatus) || STATUS_FILTERS[0])._id,
        type: "input",
        variation: "single",
      },
      {
        id: 2,
        type: "noDefaultDate",
        label: "Created Date",
        defaultValue: [startDate, endDate],
        setDate: (dates) => {
          if (Array.isArray(dates) && dates.length >= 2 && dates[0] && dates[1]) {
            setStartDate(dates[0]);
            setEndDate(dates[1]);
            setApplied({ startDate: dates[0].toDate ? dates[0].toDate() : dates[0], endDate: dates[1].toDate ? dates[1].toDate() : dates[1], status: selectedStatus });
          } else {
            setStartDate(null);
            setEndDate(null);
            setApplied({ startDate: null, endDate: null, status: selectedStatus });
          }
          setPage(0);
        },
      },
    ],
    [startDate, endDate, selectedStatus]
  );

  const columns = [
    { id: "sNo", label: "S.No", placeMent: true, disablePadding: false },
    {
      id: "certificateText",
      label: "Certificate Number",
      placeMent: false,
      disablePadding: false,
      type: "component",
      renderComponent: (row) =>
        row.hasPdf ? (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => download(row)}
            disabled={Boolean(busyRows[row.id])}
            aria-label={`Download PDF for ${row.certificateText}`}
            sx={{ fontSize: "inherit", fontFamily: "inherit", textAlign: "left", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 0.75 }}
          >
            {busyRows[row.id] === "DOWNLOAD" && <CircularProgress size={14} aria-label="Downloading PDF" />}
            {row.certificateText}
          </Link>
        ) : (
          <Typography className="TableBodyCell">{row.certificateText}</Typography>
        ),
    },
    { id: "customerText", label: "Customer Name", placeMent: true, disablePadding: false },
    { id: "productText", label: "Product", placeMent: true, disablePadding: false },
    { id: "premiumText", label: "Total Premium", placeMent: true, disablePadding: false },
    { id: "createdByText", label: "Created By", placeMent: true, disablePadding: false },
    { id: "formattedDate", label: "Created Date", placeMent: true, disablePadding: false },
    { id: "statusText", label: "Status", placeMent: true, disablePadding: false, type: "coloredText", getColor: (row) => statusColor(row.status) },
    {
      id: "apiEnv",
      label: "RoadZen",
      placeMent: false,
      disablePadding: false,
      type: "component",
      renderComponent: (row) => <Chip size="small" label={row.apiEnv} color={row.apiEnv === "TEST" ? "warning" : "success"} />,
    },
    {
      id: "action_more",
      label: "",
      placeMent: false,
      disablePadding: false,
      type: "component",
      renderComponent: (row) => (
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: "nowrap", justifyContent: "flex-end" }}>
          {rowButtons(row).map((b) => {
            const Icon = ACTION_ICONS[b.key];
            return (
              <Tooltip key={b.key} title={b.hint} placement="top" disableInteractive>
                <span>
                  <IconButton size="small" aria-label={b.label} onClick={() => onAction(b.key, row)} disabled={Boolean(busyRows[row.id])} sx={b.key === "CANCEL" ? ROW_CANCEL_SX : ROW_ACTION_SX}>
                    {busyRows[row.id] === b.key ? <CircularProgress size={14} color="inherit" aria-label="Working on this row" /> : <Icon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            );
          })}
        </Stack>
      ),
    },
  ];

  const activeFiltersCount = (startDate && endDate ? 1 : 0) + (selectedStatus ? 1 : 0);

  return (
    <div style={{ margin: "1px" }}>
      {loading && !data.length && <Loader open />}
      <Grid container className="Master_Card_Container">
        <Grid container alignItems={"center"} justifyContent={"space-between"} className="Master_Card_Sub_Container" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography className="Master_Card_Tittle">
              RoadZen Certificate List
              {apiEnv === "TEST" && <Chip size="small" color="warning" label="TEST" sx={{ ml: 1, verticalAlign: "middle" }} />}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={8} className="d-flex" justifyContent={{ xs: "start", sm: "flex-end", md: "flex-end" }}>
            <Stack direction={{ xs: "column", sm: "row", md: "row" }} spacing={2} mt={{ xs: 2, sm: 2, md: 0 }}>
              {/* the server says who may create (a user login not yet, 2026-11-03) */}
              {canCreate !== false && (
                <Button className="Master_Card_Filter_Button" sx={{ height: "40px", gap: "8px", px: 2, whiteSpace: "nowrap" }} startIcon={<AddOutlined />} onClick={() => navigate(ROADZEN_CREATE_CERT_PATH)}>
                  Create Certificate
                </Button>
              )}
              <Button
                className="Master_Card_Filter_Button w-100"
                sx={{ width: "102px", height: "40px", gap: "8px" }}
                startIcon={
                  <Badge badgeContent={activeFiltersCount} color="error">
                    <FilterListIcon sx={{ marginRight: "1.5px" }} />
                  </Badge>
                }
                onClick={() => setShowFilter(!showFilter)}
              >
                Filters
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {showFilter && (
          <Box sx={{ mb: 2, overflow: "visible", position: "relative", zIndex: 100, minHeight: "60px", width: "100%" }}>
            <FilterBox
              filterData={filterData}
              type={"single"}
              handleSaveAllFilter={() => {
                setApplied({ startDate: startDate ? (startDate.toDate ? startDate.toDate() : startDate) : null, endDate: endDate ? (endDate.toDate ? endDate.toDate() : endDate) : null, status: selectedStatus });
                setPage(0);
              }}
              handleClearAllState={() => {
                setStartDate(null);
                setEndDate(null);
                setSelectedStatus("");
                setApplied({ startDate: null, endDate: null, status: "" });
                setPage(0);
              }}
              handleClearSingleFilter={(filterId) => {
                if (filterId === 1) setSelectedStatus("");
                if (filterId === 2) {
                  setStartDate(null);
                  setEndDate(null);
                }
              }}
              horizontalLayout={true}
              hideHeader={true}
              hideInput={true}
              alwaysOpen={true}
              autoApply={true}
            />
          </Box>
        )}

        <Grid item xs={12}>
          <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1, mb: 2 }} />
        </Grid>

        <Grid container alignItems={"center"} justifyContent={"flex-end"} className="Master_Card_Sub_Container" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4} md={3.1}>
            <Input
              className="w-100 Master_Card_Input"
              type="text"
              placeholder="Search name, certificate, chassis, mobile"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              disableUnderline={true}
              startAdornment={
                <InputAdornment position="start" sx={{ marginRight: "0px" }}>
                  <IconButton onClick={() => setDebouncedSearch(search)} aria-label="search">
                    <SearchIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ width: "100%", height: 3, mb: 0.5 }}>{loading && data.length > 0 && <LinearProgress aria-label="Updating the list" sx={{ height: 3, borderRadius: 2 }} />}</Box>

        <MasterCommonTable
          data={data}
          tableTotalCount={totalCount}
          columns={columns}
          tableOutputEvent={(event) => {
            if (event.page !== undefined) setPage(event.page - 1);
            if (event.limit) setRowsPerPage(event.limit);
          }}
          handleButton={{}}
          isServerSide={true}
          limit={rowsPerPage}
          tableConfig={{ isAction: false, isSearch: false, isView: false, isEdit: false, isDelete: false, isMultiDelete: false, isDownload: false }}
        />
      </Grid>

      {cancelRow && (
        <CancelCertificateDialog
          row={cancelRow}
          onClose={() => setCancelRow(null)}
          onDone={() => {
            refreshRow(cancelRow.id, "CANCEL");
            setCancelRow(null);
          }}
        />
      )}
      {historyRow && <HistoryDialog row={historyRow} onClose={() => setHistoryRow(null)} />}
    </div>
  );
};

export default RoadzenCertificateList;
